import { Boxes, Gauge, History, LayoutDashboard, Palette, Sparkles, Users, UsersRound } from 'lucide-react'

import { paths } from './paths'
import { ADMIN_ROLES, GENERATING_ROLES, ROLES } from './routeAccess'

/**
 * THE route configuration. Every route in the application is declared here and
 * mapped into the router by `app/router.jsx`; nothing else defines a route.
 *
 * Per-route fields:
 *   layout       'dashboard' | 'auth'  which shell wraps this branch
 *   name         label for navigation, breadcrumbs and the document title
 *   path         absolute path, always taken from `paths.js`
 *   element      component factory for a lazily loaded page
 *   index        index route of its parent
 *   icon         sidebar icon (lucide component)
 *   showInNav    render a sidebar entry for this route
 *   section      'main' | 'footer' sidebar grouping
 *   roles        roles allowed to reach it (see routeAccess.js)
 *   children     nested routes; rendered inside the parent's <Outlet />
 */

/** Wraps a dynamic import so the router can code-split each page. */
const page = (loader) => async () => ({ Component: (await loader()).default })

export const routes = [
  {
    id: 'auth',
    layout: 'auth',
    public: true,
    children: [
      {
        id: 'login',
        name: 'Sign in',
        path: paths.login,
        hideCrumb: true,
        element: page(() => import('@/features/auth/pages/LoginPage')),
      },
    ],
  },
  {
    id: 'app',
    layout: 'dashboard',
    children: [
      {
        id: 'root-redirect',
        index: true,
        // Not `redirectTo`: the destination depends on the signed-in role.
        element: page(() => import('@/routes/RootRedirect')),
      },
      {
        id: 'dashboard',
        name: 'Dashboard',
        path: paths.dashboard,
        icon: LayoutDashboard,
        showInNav: true,
        section: 'main',
        // Reads the admin usage endpoints, so it matches their gate.
        roles: ADMIN_ROLES,
        element: page(() => import('@/features/dashboard/pages/DashboardPage')),
      },
      {
        id: 'studio',
        name: 'Studio',
        path: paths.studio,
        icon: Sparkles,
        showInNav: true,
        section: 'main',
        roles: GENERATING_ROLES,
        element: page(() => import('@/features/studio/pages/StudioPage')),
      },
      {
        id: 'studio-thread',
        name: 'Studio',
        path: paths.studioThread,
        hideCrumb: true,
        roles: GENERATING_ROLES,
        element: page(() => import('@/features/studio/pages/StudioPage')),
      },
      {
        id: 'history',
        name: 'History',
        path: paths.history,
        icon: History,
        showInNav: true,
        section: 'main',
        roles: GENERATING_ROLES,
        element: page(() => import('@/features/studio/pages/HistoryPage')),
      },
      {
        id: 'usage',
        name: 'Token usage',
        path: paths.usage,
        icon: Gauge,
        showInNav: true,
        section: 'main',
        // /v1/admin/* is admin-or-owner on the server; matching that here
        // keeps a member from navigating into a guaranteed 403.
        roles: ADMIN_ROLES,
        element: page(() => import('@/features/usage/pages/UsagePage')),
      },
      {
        id: 'user-usage',
        name: 'User usage',
        path: paths.userUsage,
        roles: ADMIN_ROLES,
        element: page(() => import('@/features/usage/pages/UserUsagePage')),
      },
      {
        id: 'models',
        name: 'Models',
        path: paths.models,
        icon: Boxes,
        showInNav: true,
        section: 'main',
        // Any signed-in role may list models: the endpoint filters by
        // entitlement rather than refusing outright.
        roles: [ROLES.owner, ROLES.admin, ROLES.member, ROLES.viewer],
        element: page(() => import('@/features/models/pages/ModelsPage')),
      },
      {
        id: 'people',
        name: 'People',
        path: paths.people,
        icon: Users,
        showInNav: true,
        section: 'main',
        // Matches requireRole('admin','owner') on the server, so a member
        // never navigates into a guaranteed 403.
        roles: ADMIN_ROLES,
        element: page(() => import('@/features/people/pages/UsersPage')),
      },
      {
        id: 'person-detail',
        name: 'Person',
        path: paths.personDetail,
        roles: ADMIN_ROLES,
        element: page(() => import('@/features/people/pages/UserDetailPage')),
      },
      {
        id: 'groups',
        name: 'Groups',
        path: paths.groups,
        icon: UsersRound,
        // Its own nav entry, not just a route: quotas and model access are
        // granted to GROUPS, so a page reachable only by typing its URL made
        // the whole mechanism invisible.
        showInNav: true,
        section: 'main',
        roles: ADMIN_ROLES,
        element: page(() => import('@/features/people/pages/GroupsPage')),
      },
      {
        id: 'group-detail',
        name: 'Group',
        path: paths.groupDetail,
        roles: ADMIN_ROLES,
        element: page(() => import('@/features/people/pages/GroupDetailPage')),
      },
      {
        id: 'design-system',
        name: 'Design system',
        path: paths.designSystem,
        icon: Palette,
        showInNav: true,
        section: 'footer',
        roles: ADMIN_ROLES,
        element: page(() => import('@/features/designSystem/pages/DesignSystemPage')),
      },
      {
        id: 'unauthorized',
        name: 'Access denied',
        path: paths.unauthorized,
        element: page(() => import('@/features/errors/pages/UnauthorizedPage')),
      },
      {
        id: 'not-found',
        name: 'Page not found',
        path: '*',
        hideCrumb: true,
        element: page(() => import('@/features/errors/pages/NotFoundPage')),
      },
    ],
  },
]
